import * as Sentry from "@sentry/react";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { toIso } from "./countryLanguageUtils.ts";
import { invariant } from "./invariant.ts";

const PLURAL_CATEGORIES = new Set<string>([
  "zero",
  "one",
  "two",
  "few",
  "many",
  "other",
]);

type PluralForms = Record<string, string>;

const isPluralForms = (value: object): value is PluralForms =>
  "other" in value &&
  Object.entries(value).every(
    ([category, form]) =>
      PLURAL_CATEGORIES.has(category) && typeof form === "string",
  );

// `Intl` rejects the underscore in codes like `en_US`.
const pluralCategoriesFor = (locale: string) =>
  new Intl.PluralRules(locale.replace("_", "-")).resolvedOptions()
    .pluralCategories;

// A locale can need a plural category the translations do not define — French
// has `many` for multiples of a million — and i18next then resolves no form at
// all and hands back the whole map. Filling the gaps in the translations keeps
// every lookup a string, whether the caller is the hook, the HOC or `I18n`.
//
// TODO: add the `many` form to the French plural keys in Lokalise and drop this.
const completePluralForms = (
  node: unknown,
  categories: readonly string[],
): unknown => {
  if (node === null || typeof node !== "object" || Array.isArray(node)) {
    return node;
  }

  if (isPluralForms(node)) {
    const missing = categories.filter((category) => !(category in node));

    return missing.length === 0
      ? node
      : {
          ...node,
          ...Object.fromEntries(
            missing.map((category) => [category, node.other]),
          ),
        };
  }

  // Only the handful of keys with a gap need rebuilding, so the rest of the tree
  // is handed back as it is rather than deep copied on every page load.
  let filledAGap = false;

  const entries = Object.entries(node).map(([key, value]) => {
    const completed = completePluralForms(value, categories);
    filledAGap ||= completed !== value;

    return [key, completed];
  });

  return filledAGap ? Object.fromEntries(entries) : node;
};

const resources: Record<string, { translation: object }> = {};

const localeFilesContext = require.context(
  "../../../../config/locales/",
  false,
);

for (const key of localeFilesContext.keys()) {
  const module: any = localeFilesContext(key);

  const language = key.match(
    /\.\/((countries|flowcase)\.)?(?<code>[a-z]{2}(_[A-Z]{2})?)\.yml/,
  )?.groups?.code;

  invariant(language, `Cannot extract language from ${key}`);

  const bcp47Locale = toIso(language);

  const translation = completePluralForms(
    module.default[language],
    pluralCategoriesFor(bcp47Locale),
  );

  invariant(
    translation !== null && typeof translation === "object",
    `Expected an object of translations in ${key}`,
  );

  resources[bcp47Locale] = {
    translation: { ...resources[bcp47Locale]?.translation, ...translation },
  };
}

const warnedMissingTranslations = new Set<string>();

function reportMissingTranslation(key: string) {
  if (warnedMissingTranslations.has(key)) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setExtra("scope", key);
    scope.captureException(new Error(`Missing translation: "${key}"`));
  });

  warnedMissingTranslations.add(key);
}

export const i18nextInstance = i18next.createInstance().use(initReactI18next);

// Translations are bundled rather than fetched, so `init` completes before it
// returns and there is nothing to await. The locale is applied by
// `initTranslations`, so this starts on "en" — matching i18n-js defaulting both
// locales to "en" until then.
// eslint-disable-next-line unicorn/no-top-level-side-effects
void i18nextInstance.init({
  lng: "en",
  resources,
  // A key missing from the active locale renders a marker rather than silently
  // showing another language.
  fallbackLng: false,
  load: "currentOnly",
  nsSeparator: false,
  returnObjects: true,
  returnNull: false,
  react: { useSuspense: false },
  // The translations are shared with Rails, so they use Ruby's conventions:
  // plural forms as nested `one:`/`other:` maps, which a `.` separator turns
  // into the lookups `key.one`/`key.other`.
  pluralSeparator: ".",
  interpolation: {
    // React escapes anything rendered as a child.
    escapeValue: false,
    // Ruby writes placeholders as `%{name}`, but some keys use `{{name}}`.
    // The escaped variants are injected into the interpolation regex verbatim,
    // so both spellings resolve without rewriting the translations. They only
    // apply when the plain `prefix`/`suffix` defaults are cleared.
    prefix: undefined,
    suffix: undefined,
    prefixEscaped: String.raw`(?:%\{|\{\{)`,
    suffixEscaped: String.raw`\}\}?`,
  },
  missingInterpolationHandler: (_text: string, match: RegExpMatchArray) =>
    `[missing "${match[0]}" value]`,
  parseMissingKeyHandler: (
    key: string,
    defaultValue?: string,
    options?: { lng?: string },
  ) => {
    if (defaultValue !== undefined) {
      return defaultValue;
    }

    reportMissingTranslation(key);

    const locale = options?.lng ?? i18nextInstance.language;

    return `[missing "${locale}.${key}" translation]`;
  },
});

let currentLocale = "en";
let currentDefaultLocale = "en";

// i18next defaults to "en" until this runs, so it has to happen before the
// first render rather than whenever the importing module is evaluated.
export function initTranslations({
  locale,
  defaultLocale,
}: {
  locale: string;
  defaultLocale: string;
}) {
  currentLocale = locale;
  currentDefaultLocale = defaultLocale;
  // The resources are already fully loaded, so this resolves synchronously —
  // `i18nextInstance.language` reflects `locale` before this call returns.
  void i18nextInstance.changeLanguage(toIso(locale));
}

// Our codes are translation-file keys. Intl wants a real tag, and a region with
// it: without one it picks its own, which decides date and number formats.
const localeTags: Record<string, string> = {
  dk: "da-DK",
  de: "de-DE",
  en: "en-GB",
  fi: "fi-FI",
  fr: "fr-FR",
  no: "nb-NO",
  se: "sv-SE",
};

export function toLocaleTag(locale: string) {
  return localeTags[locale] ?? locale;
}

type Key = string | readonly string[];

interface TranslateOptions {
  scope?: Key;
  locale?: string | null;
  count?: number;
  defaultValue?: string;
  // Anything else is a value to interpolate into the string. Which names a key
  // accepts cannot be checked here, because the translations reach i18next as
  // plain strings rather than as literal types.
  [placeholder: string]: Key | number | null | undefined;
}

const joinKey = (key: Key) => (typeof key === "string" ? key : key.join("."));

// Translates i18n-js' spelling of the options into i18next's. Nothing else
// belongs here: this only exists until every call site has moved to the hook or
// the HOC, so anything it did would be lost at that point.
function translate<T = string>(
  key: Key,
  options: TranslateOptions = {},
): string | T {
  const { scope, locale, count, defaultValue, ...placeholders } = options;

  return i18nextInstance.t(
    scope === undefined ? joinKey(key) : `${joinKey(scope)}.${joinKey(key)}`,
    {
      lng: locale == null ? undefined : toIso(locale),
      count,
      defaultValue,
      // Placeholder values go through `replace` so that one named like an
      // i18next option (`context`, `ns`, …) cannot change how the key resolves.
      replace: { ...placeholders, count },
    },
  );
}

export const I18n = {
  t: translate,
  get locale(): string {
    return currentLocale;
  },
  get defaultLocale(): string {
    return currentDefaultLocale;
  },
};
